/**
 * Maps form question definitions to the DocuSign tab structure they produce.
 * Static analysis based on question type — no submission data required.
 *
 * Mirrors the tab-building logic in buildTabsFromFormSubmission (server-side).
 */

export type DocusignTabType = 'textTab' | 'checkboxTab' | 'radioGroupTab' | 'none';

export interface MappedDocusignTab {
  questionRef: string;
  questionTitle: string;
  questionType: string;
  tabType: DocusignTabType;
  /** Tab label(s) or group name(s) that appear in the DocuSign payload */
  tabLabels: string[];
  sectionTitle: string;
  subsectionTitle: string;
  hasComposite: boolean;
}

export function mapFormQuestionsToDocusignTabs(
  formDefinition: PolicyFormDefinition,
): MappedDocusignTab[] {
  // Build location map: questionRef → { sectionTitle, subsectionTitle }
  const locationMap = new Map<string, { sectionTitle: string; subsectionTitle: string }>();
  for (const section of formDefinition.sections) {
    for (const subsection of section.subsections) {
      for (const group of subsection.questionGroups) {
        for (const questionRef of group.questions) {
          locationMap.set(questionRef, {
            sectionTitle: section.title || 'Untitled Section',
            subsectionTitle: subsection.title || 'Untitled Subsection',
          });
        }
      }
    }
  }

  const result: MappedDocusignTab[] = [];

  for (const [questionRef, question] of Object.entries(formDefinition.questions)) {
    const location = locationMap.get(questionRef) ?? { sectionTitle: '—', subsectionTitle: '—' };

    const base = {
      questionRef,
      questionTitle: question.title || questionRef,
      questionType: question.type,
      sectionTitle: location.sectionTitle,
      subsectionTitle: location.subsectionTitle,
      hasComposite: !!question.composite,
    };

    switch (question.type) {
      case 'text':
      case 'number':
      case 'textarea':
      case 'textarea-text':
      case 'date':
      case 'textselect':
        result.push({ ...base, tabType: 'textTab', tabLabels: [questionRef] });
        break;

      case 'multiselect':
        result.push({
          ...base,
          tabType: 'checkboxTab',
          tabLabels:
            question.options && question.options.length > 0
              ? question.options.map(opt => `${questionRef}-${opt.id}`)
              : [`${questionRef}-*`],
        });
        break;

      case 'select':
      case 'radio':
        result.push({ ...base, tabType: 'radioGroupTab', tabLabels: [questionRef] });
        break;

      case 'table':
        if (question.columns && question.columns.length > 0) {
          result.push({
            ...base,
            tabType: 'textTab',
            // N = row index placeholder — actual labels are {questionRef}-{columnId}-{rowIndex+1}
            tabLabels: question.columns.map(col => `${questionRef}-${col.id}-N`),
          });
        } else {
          result.push({ ...base, tabType: 'none', tabLabels: [] });
        }
        break;

      default:
        result.push({ ...base, tabType: 'none', tabLabels: [] });
    }
  }

  return result;
}
